import * as React from 'react';
import {connect} from 'react-redux';
import {Dispatch} from 'redux';

import {EXTERNAL_SERVICE_INTEGRATIONS_ENABLED} from '../feature-flags';
import * as testerIcon from '../images/testerIcon.png';
import {configTesterDispatchToPropsObject, globalDispatchToPropsObject} from '../../store';
import FileSelector from './FileSelector';
import {
	Tabs, TabOption,
	Icon,
	ExternalServiceIntegrations,
	EMPTY_EXTERNAL_SERVICE_INTEGRATION_REQUEST,
	isDeployEnabledPerRaterTypeNumberOfRaters,
	isDeployEnabledPerRaterTypeValidFields,
	BoundedContainer,
	SplitPaneHorizontal,
} from '../util';
import DeploymentStatus from './DeploymentStatus';
import {renewAuthenticationIfAboutToExpire} from '../auth';

interface StateProps {
	user: UserState;
	username: string;
	lastTenantNameSuffix: string;
	deployType: ConfigTester.DeployType;
	raterType: RaterType;
	deployResponse: ConfigTester.DeployResponse | null;
	deployErrorMessage: string | null;
}

interface DispatchProps {
	renewAuth(authToken: string): Promise<AuthenticateResponse>;
	clearAuthIfExpired(): void;
	deploy(
		validationRequest: ConfigTester.ValidationRequest,
		deployTestRequest: ConfigTester.DeployAssetsRequest,
		externalServiceIntegrationsRequest: ConfigTester.UpdateExternalServiceIntegrationsRequest,
	): Promise<ConfigTester.DeployResult>;
	setDeployType(deployType: ConfigTester.DeployType): void;
	setRaterType(raterType: RaterType): void;
	setDeployErrorMessage(errorMessage: string): void;
}

type Props = StateProps & DispatchProps;

interface State {
	tenantNameSuffix: string;
	zipFile: File | null;
	externalServiceIntegrations: ExternalServiceIntegrationRequest[];
	deployStatus: ConfigTester.DeployStatus | null;
	showDeployStatus: boolean;
}

function mapStateToProps({configTester, global}: StudioState): StateProps {
	const {assets} = configTester;
	return {
		user: global.user,
		username: global.user.username,
		lastTenantNameSuffix: assets.lastTenantNameSuffix,
		deployType: assets.deployType,
		raterType: assets.raterType,
		deployResponse: assets.deployResponse,
		deployErrorMessage: assets.errorMessage,
	};
}

function mapDispatchToProps(dispatch: Dispatch<StudioState>): DispatchProps {
	const {
		deploy,
		setDeployType,
		setRaterType,
		setDeployErrorMessage,
	} = configTesterDispatchToPropsObject;
	const {renewAuthentication, clearAuthenticationIfExpired} = globalDispatchToPropsObject;
	return {
		renewAuth: authToken => dispatch(renewAuthentication(authToken)),
		clearAuthIfExpired: () => dispatch(clearAuthenticationIfExpired()),
		setDeployType: deployType => dispatch(setDeployType(deployType)),
		setRaterType: raterType => dispatch(setRaterType(raterType)),
		deploy: (validationRequest, deployRequest, externalServiceIntegrationsRequest) => {
			return dispatch(deploy(validationRequest, deployRequest, externalServiceIntegrationsRequest));
		},
		setDeployErrorMessage: errorMessage => dispatch(setDeployErrorMessage(errorMessage)),
	};
}

function getRaterTypeOptions(): TabOption[] {
	return [
		{value: 'SOCOTRA_RATER', label: 'No'},
		{value: 'EXTERNAL_RATER', label: `Yes`, labelIcon: 'chevron-right'},
	];
}

function getTenantNameFormProperties(deployType: ConfigTester.DeployType) {
	switch (deployType) {
		case 'LAST':
			return {label: 'Previous Instance:', placeholder: ''};
		case 'LOAD':
			return {label: 'Specify Instance Name:', placeholder: ''};
		case 'TEST':
			return {label: 'Specify Instance Name:', placeholder: 'Leave blank to have an instance name generated for you'};
		default:
			return {label: '', placeholder: ''};
	}
}

function isDeployEnabledPerDeployType(deployType: string, tenantNameSuffix: string) {
	return deployType === 'LOAD' ? tenantNameSuffix.length > 0 : true;
}

function getDeployResultMinPaneSize(
	deployStatus: ConfigTester.DeployStatus,
	deployResponse: ConfigTester.DeployResponse,
): number {
	if (deployStatus === 'DEPLOYMENT_FAILURE') {
		if (deployResponse) {
			return 400;
		} else {
			return 300;
		}
	} else {
		return null;
	}
}

class ConfigTester extends React.Component<Props, State> {

	public state: State = {
		zipFile: null,
		tenantNameSuffix: '',
		externalServiceIntegrations: [EMPTY_EXTERNAL_SERVICE_INTEGRATION_REQUEST],
		deployStatus: null,
		showDeployStatus: true,
	};

	constructor(props: Props, context: any) {
		super(props, context);
		this.handleChangeZipFile = this.handleChangeZipFile.bind(this);
		this.handleChangeTenantNameSuffix = this.handleChangeTenantNameSuffix.bind(this);
		this.handleChangeDeployType = this.handleChangeDeployType.bind(this);
		this.handleChangeRaterType = this.handleChangeRaterType.bind(this);
		this.handleSubmit = this.handleSubmit.bind(this);
		this.setDeployStatus = this.setDeployStatus.bind(this);
		this.collapseDeployResult = this.collapseDeployResult.bind(this);
		this.showDeployResult = this.showDeployResult.bind(this);
		this.handleChangeExternalServiceIntegrations = this.handleChangeExternalServiceIntegrations.bind(this);
	}

	public componentDidMount() {
		this.props.clearAuthIfExpired();
		this.props.renewAuth(this.props.user.authorization.authorizationToken);
	}

	public componentDidUpdate(prevProps: Props, prevState: State) {
		if (this.state.zipFile !== prevState.zipFile) {
			this.handleSubmit();
		}
		this.props.clearAuthIfExpired();
		renewAuthenticationIfAboutToExpire(this.props.user, this.props.renewAuth);
	}

	public render() {
		const {deployStatus, showDeployStatus} = this.state;
		const {deployResponse} = this.props;
		if (!!deployStatus && !!showDeployStatus && deployStatus !== 'LOADING') {
			return <SplitPaneHorizontal minSize={getDeployResultMinPaneSize(deployStatus, deployResponse)}>
				<BoundedContainer className='config-tester'>
					{this.renderTopContent()}
				</BoundedContainer>
				{this.renderDeploymentStatus()}
			</SplitPaneHorizontal>;
		} else {
			return <BoundedContainer className='config-tester'>
				{this.renderTopContent()}
			</BoundedContainer>;
		}
	}

	private renderTopContent() {
		if (this.props.raterType === 'EXTERNAL_RATER') {
			return <div className='row-expanded'>
				<div className='column medium-6'>
					{this.renderDeployForm()}
				</div>
				<div className='column medium-6'>
					<ExternalServiceIntegrations
						externalServiceIntegrations={this.state.externalServiceIntegrations}
						onChange={this.handleChangeExternalServiceIntegrations}
					/>
				</div>
			</div>;
		} else {
			return <div className='row-expanded'>
				{this.renderDeployForm()}
			</div>;
		}
	}

	private renderDeployForm() {
		return <div className='deploy deploy-form'>
			{this.renderIconHeader()}
			<hr className='application-icon-rule' />
			{this.renderDeployDescription()}
			{this.renderDeployOptions()}
			{this.renderUseExternalRaterOptions()}
			{this.renderSpecifyTenantNameForm()}
			{this.renderFileSelector()}
			{this.renderShowDeployResultLink()}
		</div>;
	}

	private renderIconHeader() {
		return <div className='application-icon-container'>
			<img src={testerIcon} width={50} />
			<h2 className='bolder'>Configuration Tester</h2>
		</div>;
	}

	private renderDeployDescription() {
		return <ul className='list-socotra-green--less-spaced'>
			<li>Iterate quickly on configuration stored elsewhere.</li>
			<li>Deploy configuration to an existing test instance or create a new one.</li>
		</ul>;
	}

	private renderFileSelector() {
		return <FileSelector
			dropzoneErrorMessage={this.getDropzoneErrorMessage()}
			onReceiveFile={this.handleChangeZipFile}
			setDeployStatus={this.setDeployStatus}
			setProcessFileError={this.props.setDeployErrorMessage}
		/>;
	}

	private renderDeployOptions() {
		return <Tabs
			contentLabel='config-tester-deploy-type'
			title='Deploy to:'
			options={this.getDeployOptions()}
			onSelect={this.handleChangeDeployType}
			selectedOption={this.props.deployType}
		/>;
	}

	private renderSpecifyTenantNameForm() {
		return <div>
			<label className='specify-tenant-label'>
				{getTenantNameFormProperties(this.props.deployType).label}
			</label>
			<div className='specify-tenant'>
				<div className='specify-tenant-username'>
					<p>{this.props.username}-</p>
				</div>
				<div className='specify-tenant-input'>
					<input
						type='text'
						value={this.getDeployTenantNameSuffix()}
						disabled={this.props.deployType === 'LAST'}
						onChange={this.handleChangeTenantNameSuffix}
						placeholder={getTenantNameFormProperties(this.props.deployType).placeholder}
					/>
				</div>
			</div>
		</div>;
	}

	private renderUseExternalRaterOptions() {
		if (EXTERNAL_SERVICE_INTEGRATIONS_ENABLED) {
			return <Tabs
				contentLabel='config-tester-use-external-rater'
				title='Use External Rater:'
				options={getRaterTypeOptions()}
				onSelect={this.handleChangeRaterType}
				selectedOption={this.props.raterType}
			/>;
		} else {
			return null;
		}
	}

	private renderShowDeployResultLink() {
		if (
			(this.state.deployStatus === 'DEPLOYMENT_FAILURE'
			|| this.state.deployStatus === 'DEPLOYMENT_SUCCESS')
			&& !this.state.showDeployStatus
		) {
			return <div className='show-deploy-status'>
				<a onClick={this.showDeployResult}>
					Show Deploy Result <Icon name='expand' />
				</a>
			</div>;
		} else {
			return null;
		}
	}

	private renderDeploymentStatus() {
		return <div className='deployment-status-container'>
			<DeploymentStatus
				deployResponse={this.props.deployResponse}
				deployErrorMessage={this.props.deployErrorMessage}
				deployStatus={this.state.deployStatus}
				onCollapse={this.collapseDeployResult}
			/>
		</div>;
	}

	private handleChangeDeployType(deployType: ConfigTester.DeployType) {
		this.props.setDeployType(deployType);
	}

	private handleChangeZipFile(file: File) {
		this.setState({zipFile: file});
	}

	private handleChangeTenantNameSuffix(event: React.FormEvent<HTMLInputElement>) {
		event.preventDefault();
		this.setState({tenantNameSuffix: event.currentTarget.value});
	}

	private handleChangeRaterType(raterType: RaterType) {
		this.props.setRaterType(raterType);
	}

	private handleSubmit() {
		const {zipFile, externalServiceIntegrations} = this.state;
		const {username, user: {authorization}, deploy} = this.props;
		const {authorizationToken} = authorization;
		const tenantNameSuffix = this.getDeployTenantNameSuffix();
		const tenantName = tenantNameSuffix ? `${username}-${tenantNameSuffix}` : null;
		const validationRequest: ConfigTester.ValidationRequest = {
			tenantName,
			authorizationToken,
		};
		const deployRequest: ConfigTester.DeployAssetsRequest = tenantNameSuffix
			? {zipFile, authorizationToken, tenantNameSuffix}
			: {zipFile, authorizationToken};
		const externalServiceIntegrationsRequest = tenantNameSuffix
			? {externalServiceIntegrations, authorizationToken, tenantNameSuffix}
			: {externalServiceIntegrations, authorizationToken};

		deploy(validationRequest, deployRequest, externalServiceIntegrationsRequest)
			.then(() => {
				this.setState({
					deployStatus: 'DEPLOYMENT_SUCCESS',
					tenantNameSuffix: '',
					showDeployStatus: true,
				});
			})
			.catch(() => {
				this.setState({
					deployStatus: 'DEPLOYMENT_FAILURE',
					showDeployStatus: true,
				});
			});
	}

	private handleChangeExternalServiceIntegrations(externalServiceIntegrations: ExternalServiceIntegrationRequest[]) {
		this.setState({
			...this.state,
			externalServiceIntegrations,
		});
	}

	private setDeployStatus(deployStatus: ConfigTester.DeployStatus) {
		this.setState({
			...this.state,
			deployStatus,
		});
	}

	private collapseDeployResult() {
		this.setState({
			...this.state,
			showDeployStatus: false,
		});
	}

	private showDeployResult() {
		this.setState({
			...this.state,
			showDeployStatus: true,
		});
	}

	private getDeployTenantNameSuffix() {
		return this.props.deployType === 'LAST' ? this.props.lastTenantNameSuffix : this.state.tenantNameSuffix;
	}

	private getDeployOptions(): TabOption[] {
		const DEPLOY_OPTIONS = [
			{value: 'TEST', label: 'Create new instance'},
			{value: 'LOAD', label: 'Specify existing instance'},
		];
		return !!this.props.lastTenantNameSuffix
			? [{value: 'LAST', label: 'Previous instance'}, ...DEPLOY_OPTIONS]
			: DEPLOY_OPTIONS;
	}

	private getDropzoneErrorMessage() {
		const enabledPerDeployType = isDeployEnabledPerDeployType(
			this.props.deployType,
			this.state.tenantNameSuffix,
		);
		const enabledPerRaterTypeNumberOfRaters = isDeployEnabledPerRaterTypeNumberOfRaters(
			this.props.raterType,
			this.state.externalServiceIntegrations,
		);
		const enabledPerRaterTypeValidFields = isDeployEnabledPerRaterTypeValidFields(
			this.props.raterType,
			this.state.externalServiceIntegrations,
		);
		if (!enabledPerDeployType) {
			if (!enabledPerRaterTypeNumberOfRaters) {
				return 'Specify instance name and add at least one external rater, then drop config to deploy';
			} else if (!enabledPerRaterTypeValidFields) {
				return 'Specify instance name and fill out required external rater fields, then drop config to deploy';
			} else {
				return 'Specify instance name, then drop config to deploy';
			}
		} else if (!enabledPerRaterTypeNumberOfRaters) {
			return 'Add at least one external rater, then drop config to deploy';
		} else if (!enabledPerRaterTypeValidFields) {
			return 'Fill out required external rater fields, then drop config to deploy';
		} else {
			return null;
		}
	}

}

export default connect<StateProps, DispatchProps, {}>
	(mapStateToProps, mapDispatchToProps)(ConfigTester);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./ts/components/config-tester/index.tsx